import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { Button } from '@calendly/ui/components/button';
import { AddIcon, CloseIcon } from '@calendly/ui/components/icons';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { DatePopup } from 'components/availability/form/date_specific_rules/date_popup';
import { timeNotationSelector } from 'selectors/current_user';
import { formatTime, parseTime, shortUserDateRangeFormat, userDateFormat } from 'services/time_format';
import { RulesContainer } from './styles';
import { getRules } from '../../utils';
const dateContainerIsDesktopUpCss = "d12ysen0";
const dateContainerCss = "dgi9net";
const DateContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isDesktopUp = useDeviceType('isDesktopUp');
  return <Component ref={ref} {...restProps} className={classNames(dateContainerCss, {
    [dateContainerIsDesktopUpCss]: isDesktopUp
  }, props.className)} />;
});
const bodyTextWrapperCss = "b1gjotao";
const BodyTextWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyTextWrapperCss, props.className)} />;
});
const spacingWrapperCss = "srb3ra7";
const SpacingWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(spacingWrapperCss, props.className)} />;
});
const rulesTableCss = "ri4nbkd";
const RulesTable = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'table';
  return <Component ref={ref} {...restProps} className={classNames(rulesTableCss, props.className)} />;
});
const ruleContainerCss = "r83own2";
const RuleContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'tr';
  return <Component ref={ref} {...restProps} className={classNames(ruleContainerCss, props.className)} />;
});
const cellAlignRightCss = "c146tb7n";
const cellCenteredCss = "c98emxz";
const cellResponsiveCss = "c8vswll";
const cellIsDesktopUpCss = "cx2hbtx";
const cellPaddingRightCss = "c12uaiod";
const cellPaddingLeftCss = "c165p2oi";
const cellCss = "csd7xm3";
const Cell = forwardRef((props, ref) => {
  const {
    centered,
    responsive,
    align,
    padding,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'td';
  const isDesktopUp = useDeviceType('isDesktopUp');
  return <Component ref={ref} {...restProps} className={classNames(cellCss, {
    [cellAlignRightCss]: align?.toString() === 'right',
    [cellCenteredCss]: centered,
    [cellResponsiveCss]: responsive,
    [cellIsDesktopUpCss]: isDesktopUp,
    [cellPaddingRightCss]: padding?.toString() === 'right',
    [cellPaddingLeftCss]: padding?.toString() === 'left'
  }, props.className)} />;
});
const intervalsCss = "ioh4e1v";
const Intervals = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(intervalsCss, props.className)} />;
});
const intervalWrapperCss = "i1rsopn";
const IntervalWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(intervalWrapperCss, props.className)} />;
});
const deleteButtonCss = "d1y5rw0r";
const DeleteButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(deleteButtonCss, props.className)} />);
const Date = ({
  range: dateRange
}) => {
  const fromDate = moment(dateRange[0]);
  const toDate = moment(dateRange[dateRange.length - 1]);
  if (dateRange.length === 1) {
    return <div>
        {userDateFormat(fromDate, 'american', 'date_small_with_year')}
      </div>;
  }
  const [rangeStart, rangeEnd] = shortUserDateRangeFormat(fromDate, toDate);
  return <div>
      {rangeStart}
      {' – '}
      {rangeEnd}
    </div>;
};
const Interval = ({
  from,
  to,
  timeNotation
}) => {
  const intervalStart = parseTime(from);
  const intervalEnd = parseTime(to);
  return <IntervalWrapper>
      {formatTime(intervalStart, timeNotation)}
      {' – '}
      {formatTime(intervalEnd, timeNotation)}
    </IntervalWrapper>;
};
export const DateSpecificSection = ({
  className,
  days,
  dates,
  setDates,
  editingDates,
  setEditingDates,
  timezone,
  responsive = true,
  displayTitle = true
}) => {
  const timeNotation = useSelector(timeNotationSelector);
  const activeDates = getRules(dates);
  return <>
      <DateContainer data-component="date-container" className={className}>
        <BodyTextWrapper>
          {displayTitle && (responsive ? <HeadingText size="small" level={3}>{I18n.t('publisher.availability.schedules.date_specific_hours')}</HeadingText> : <BodyText weight="bold">{I18n.t('publisher.availability.schedules.date_specific_hours')}</BodyText>)}
          {(displayTitle || activeDates.length === 0) && <SpacingWrapper>
              <BodyText color="muted">
                {I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.dates_differ')}
              </BodyText>
            </SpacingWrapper>}
        </BodyTextWrapper>
        <div>
          <Button onClick={() => setEditingDates([])} decoration="secondary-outline" size="small" spacingBottom={12}>
            <AddIcon width={12} />
            {I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.add_hours')}
          </Button>
        </div>
        <RulesContainer data-component="rules-container">
          <RulesTable>
            <tbody>
              {activeDates.map(({
              dates: dateRange,
              intervals
            }) => <RuleContainer key={nanoid(5)}>
                  <Cell padding="left" responsive={responsive} aria-label={I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.edit_date_specific_hours_label', {
                dateRange
              })} onClick={() => setEditingDates(dateRange)}>
                    <Date range={dateRange} />
                  </Cell>
                  <Cell onClick={() => setEditingDates(dateRange)}>
                    <Intervals>
                      {intervals.length === 0 && <BodyText color="muted">
                          {I18n.t('publisher.event_types.availability.unavailable')}
                        </BodyText>}
                      {intervals.map(({
                    from,
                    to
                  }) => <Interval key={`${from}-${to}`} from={from} to={to} timeNotation={timeNotation} />)}
                    </Intervals>
                  </Cell>
                  <Cell align="right" padding="right" centered responsive={responsive}>
                    <DeleteButton aria-label={I18n.t('publisher.event_type_editor.sections.available_hours.date_overrides.remove_date_specific_hours_label', {
                  dateRange
                })} onClick={() => setDates(dates.filter(({
                  date
                }) => !dateRange.includes(date)))}>
                      <CloseIcon />
                    </DeleteButton>
                  </Cell>
                </RuleContainer>)}
            </tbody>
          </RulesTable>
        </RulesContainer>
      </DateContainer>
      {editingDates && <DatePopup onClose={() => setEditingDates(null)} timezone={timezone} defaultDays={editingDates} days={days} dates={dates} setDates={setDates} />}
    </>;
};

require("./date_specific_section.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./date_specific_section.js");